package dev.krno.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

/**
 * A 4-dot orange trail that lags behind the mouse cursor. Adds a Persona
 * menu-selector "signal" to the pointer without replacing the custom SVG
 * cursor. Renders DOM elements imperatively so Compose doesn't try to
 * recompose them 60× per second on mousemove.
 *
 * CSS side handles hiding on touch-only devices via @media (hover: none).
 */
@Composable
fun CursorTrail() {
    DisposableEffect(Unit) {
        val body = document.body ?: return@DisposableEffect onDispose { }

        val count = 4
        val dots = Array(count) { i ->
            val d = document.createElement("div") as HTMLElement
            d.classList.add("cursor-trail")
            d.classList.add("cursor-trail-$i")
            body.appendChild(d)
            d
        }

        // (x, y) for each dot, off-screen until first movement.
        val positions = Array(count) { doubleArrayOf(-200.0, -200.0) }
        var targetX = -200.0
        var targetY = -200.0
        var seenMove = false

        val onMove: (Event) -> Unit = { evt ->
            val me = evt.asDynamic()
            targetX = (me.clientX as Number).toDouble()
            targetY = (me.clientY as Number).toDouble()
            if (!seenMove) {
                seenMove = true
                dots.forEach { it.classList.add("visible") }
            }
        }
        val onLeave: (Event) -> Unit = {
            dots.forEach { it.classList.remove("visible") }
        }
        val onEnter: (Event) -> Unit = {
            if (seenMove) dots.forEach { it.classList.add("visible") }
        }

        window.addEventListener("mousemove", onMove)
        document.addEventListener("mouseleave", onLeave)
        document.addEventListener("mouseenter", onEnter)

        var rafId = 0
        val ease = 0.32
        lateinit var tick: () -> Unit
        tick = tick@{
            // head follows the cursor with same easing as the trail — feels smooth
            positions[0][0] += (targetX - positions[0][0]) * ease
            positions[0][1] += (targetY - positions[0][1]) * ease
            for (i in 1 until count) {
                positions[i][0] += (positions[i - 1][0] - positions[i][0]) * ease
                positions[i][1] += (positions[i - 1][1] - positions[i][1]) * ease
            }
            for (i in 0 until count) {
                val x = positions[i][0]
                val y = positions[i][1]
                dots[i].style.asDynamic().transform =
                    "translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)"
            }
            rafId = window.requestAnimationFrame { tick() }
        }
        rafId = window.requestAnimationFrame { tick() }

        onDispose {
            window.removeEventListener("mousemove", onMove)
            document.removeEventListener("mouseleave", onLeave)
            document.removeEventListener("mouseenter", onEnter)
            window.cancelAnimationFrame(rafId)
            dots.forEach { it.remove() }
        }
    }
}
